import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import propTypes from "prop-types";
import useSound from "use-sound";
import { FiAlertOctagon, FiAlertTriangle, FiCheck } from "react-icons/fi";

import goodSfx from '../../../assets/good.mp3';
import badSfx from '../../../assets/bad.mp3';
import busySfx from '../../../assets/busy.mp3';
import swapSfx from '../../../assets/swap.mp3';

import flex from "../../../styles/flex";

import { Booking, api, AssetLocation, KeyMangerBarcodeResultType, IAssetKeysAtLocationPath, RequestStatus, ErrorToString, IAssetVehiclesForAuditingCountResult, IAssetVehiclesForAuditingAtLocationId, AuditKeyStatusType } from "../../../logic/api";

import BarcodeScanner from "../../../components/thirdParty/react-barcode-reader";
import AuditListItem from "../../../components/lists/listItems/AuditListItem/AuditListItem";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";

import useWindowSize from "../../../utility/useWindowSize";
import { SessionContext } from "../../../utility/useSession";
import usePaths from "../../../utility/paths";
import { useDate } from "../../../utility/useDate";
import { Trans, useTranslation } from "react-i18next";
import { formatVehicle, formatVehicleFull } from "../../../utility/utils";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";

const KeyAuditScanner: React.FunctionComponent<{
    location: AssetLocation;
    show: boolean;
    enforceKeyPositionAudit?: boolean | undefined;
    onClose: (submitted: boolean) => void;
}> = (props) => {
    const paths = usePaths();
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const [scanned, setScanned] = useState<Booking[]>([]);
    const [barcode, setBarcode] = useState("");
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>();
    const fetchScanData = api.hooks.keyManager.barcodeScanKeyAudit();
    const fetchCount = api.hooks.assetVehicleKey.countAtLocation();
    const [params, setParams] = useState<IAssetKeysAtLocationPath | null>(null);

    const isEnabledEnforceKeyPositionAudit = props.enforceKeyPositionAudit == true;
    const isReleasedScanLocationToCompleteKeyAudit = useReleaseManagement(ReleaseFlag.releaseScanLocationToCompleteKeyAudit);

    const fetchCountV2 = api.hooks.keyManager.countAtLocation();
    const [expectedParams, setExpectedParams] = useState<IAssetVehiclesForAuditingAtLocationId | null>(null);
    const [missingParams, setMissingParams] = useState<IAssetVehiclesForAuditingAtLocationId | null>(null);
    const [removeKey, setRemoveKey] = useState<Booking | null>(null);
    const submitKeyAudit = api.hooks.keyManager.submitKeyAudit();
    const [showSubmitKeyAudit, setShowSubmitKeyAudit] = useState(false);
    const { localizeDate } = useDate();

    const reloadRemainingKeys = (scannedItems: Booking[]) => {
        const ids: number[] = [];
        for (const s of scannedItems) {
            ids.push(s.id);
        }
        setParams({
            path: props.location.path,
            excludedBookingIds: ids,
        });
        setExpectedParams({
            locationId: props.location.id,
            excludedBookingIds: ids,
            type: AuditKeyStatusType.expected,
        });
        setMissingParams({
            locationId: props.location.id,
            excludedBookingIds: ids,
            type: AuditKeyStatusType.missing,
        });
    }

    const [scanBusy, setScanBusy] = useState(false);
    const [goodSound] = useSound(goodSfx);
    const [badSound] = useSound(badSfx);
    const [busySound] = useSound(busySfx);
    const [unexpectedSound] = useSound(swapSfx);
    const windowSize = useWindowSize();
    const [currentKeyPosition, setCurrentKeyPosition] = useState(0);
    const [showMissingKeyMenu, setShowMissingKeyMenu] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [missingKeys, setMissingKeys] = useState<Booking[]>([]);

    const isMarkedAsMissing = (booking: Booking) => {
        return missingKeys.find((b) => b.id == booking.id) != null;
    }

    const isMatchedScan = (booking: Booking) => {
        const vehicleKeyLocationIsMatched = booking.vehicleKey?.location?.id == props.location.id;
        const vehicleLocationIsMatched = booking.vehicle?.location?.id == props.location.id;
        return isEnabledEnforceKeyPositionAudit ? vehicleLocationIsMatched : vehicleKeyLocationIsMatched;
    }

    useEffect(() => {
        if (props.show == true) {
            setErr(null);
            setMsg(null);
            setScanned([]);
            setBarcode("");
            setRemoveKey(null);
            reloadRemainingKeys([]);
            setScanBusy(false);
            setCurrentKeyPosition(0);
            setSelectedBooking(null);
            setShowMissingKeyMenu(false);
            setMissingKeys([]);
            if (isEnabledEnforceKeyPositionAudit) {
                fetchCountV2.send(session.current.context, {
                    locationId: props.location.id
                });
            } else {
                fetchCount.send(session.current.context, {
                    path: props.location.path
                });
            }

        }
    }, [props.show]);

    const removeSelectedKey = (result: boolean) => {
        const key = removeKey;
        setScanBusy(false);
        setRemoveKey(null);
        if (key == null) {
            return;
        }
        if (result == false) {
            return;
        }
        const scannedItems = scanned.filter((b) => {
            return b.id != key.id;
        })
        setScanned(scannedItems);
        reloadRemainingKeys(scannedItems);
        setErr(null);
        setMsg(t("messages.keyRemovedFromAudit"));
    }

    const onBarcodeScanned = async (scannedBarcode: string) => {
        if (scanBusy == true) {
            busySound();
            return;
        }
        setScanBusy(true);
        if ((await processScanned(scannedBarcode)) == true) {
            setScanBusy(false);
        }
    }

    const getCurrentKeyPosition = (missingKeys: Booking[]) => {
        if (isEnabledEnforceKeyPositionAudit == false) return 0;
        let pos = currentKeyPosition + 1;
        for (let i = 0; i < missingKeys.length; i++) {
            if (isMatchedScan(missingKeys[i]) && missingKeys[i].vehicle?.row == pos) {
                pos++;
            }
        }
        return pos;
    }

    const processScanned = async (scannedBarcode: string) => {
        scannedBarcode = `${scannedBarcode ?? ""}`.trim();

        if (isReleasedScanLocationToCompleteKeyAudit) {
            if (scannedBarcode === props.location.code) {
                return new Promise<boolean>((accept) => {
                    submit();
                    accept(true);
                    return;
                })
            }
        }

        return new Promise<boolean>((accept) => {
            setErr(null);
            setMsg(null);
            if (scannedBarcode.length == 0) {
                accept(true);
                return;
            }
            setBarcode("");

            let nKeyPos = getCurrentKeyPosition(missingKeys);

            fetchScanData.send(session.current.context, {
                scan: scannedBarcode,
                locationId: location == null ? null : props.location.id,
                row: isEnabledEnforceKeyPositionAudit ? nKeyPos : undefined,
                scanDate: localizeDate(new Date()).toISO() ?? ""
            }, (result) => {

                if (result == null) {
                    badSound({ forceSoundEnabled: true });
                    accept(true);
                    return;
                }

                if (result.type == KeyMangerBarcodeResultType.booking) {
                    if (location == null) {
                        badSound({ forceSoundEnabled: true });
                        setErr(t("errors.scanLocation"));
                        accept(true);
                        return;
                    }
                    if (result.booking == null) {
                        setErr(t("errors.keyBarcodeNotFound"));
                        badSound({ forceSoundEnabled: true });
                        accept(true);
                        return;
                    }

                    const exists = scanned.find((b) => result.booking == null || result.booking.id == b.id);

                    if (exists != null) {
                        setErr(t("errors.keyAlreadyScanned"));
                        if (isEnabledEnforceKeyPositionAudit == false) setRemoveKey(result.booking);
                        badSound({ forceSoundEnabled: true });
                        accept(true);
                        return;
                    }

                    const locationIsMatched = isMatchedScan(result.booking);

                    const validateKeyPositionForEnforcedAuditing = (nKeyPos: number) => {
                        //Ensures that the key position of the booking matches the current expected key position defined by nKeyPos
                        if (locationIsMatched == false) return true;
                        const pos = (result.booking?.vehicle?.row ?? 0);
                        if (pos == 0) return true;
                        return pos == nKeyPos;
                    }

                    if (isEnabledEnforceKeyPositionAudit && missingKeys.length > 0 && missingKeys[missingKeys.length - 1].id == result.booking.id && result.booking?.id != null) {
                        //remove the key previously marked as missing from the missing keys list
                        const newMissingKeys = missingKeys.filter((b) => b.id != result.booking?.id);
                        const tmpKeyPos = getCurrentKeyPosition(newMissingKeys);

                        if (validateKeyPositionForEnforcedAuditing(tmpKeyPos) == true) {
                            //Only remove the key from the missing keys list if the key position is correct
                            nKeyPos = tmpKeyPos;
                            setMissingKeys(newMissingKeys);
                        }
                    }

                    if (isEnabledEnforceKeyPositionAudit && validateKeyPositionForEnforcedAuditing(nKeyPos) == false) {
                        //Key position is not correct
                        if (result?.expectedVehicleAtRow != null) {
                            //Key position is not correct and expected vehicle details are available
                            setErr(t("errors.keyPositionAuditErrorWithVehicleDetails", {
                                current: nKeyPos,
                                row: result.booking.vehicle.row,
                                vehicle: formatVehicleFull(result!.expectedVehicleAtRow!),
                            }));
                        } else {
                            //Key position is not correct and expected vehicle details are not available
                            setErr(t("errors.keyPositionAuditError", {
                                current: nKeyPos,
                                row: result.booking.vehicle.row,
                            }));
                        }
                        badSound({ forceSoundEnabled: true });
                        accept(true);
                        return;
                    }

                    const scannedItems = [...[result.booking], ...scanned];
                    setScanned(scannedItems);
                    if (isEnabledEnforceKeyPositionAudit == true && locationIsMatched == false) {
                        //Key position is correct but location is not matched
                        setErr(t("messages.unexpectedKeyAudited", { ref: result.booking.ref, location: props.location.name }));
                        //Use a bad sound since we are using the key position audit
                        badSound({ forceSoundEnabled: true });
                    } else if (isEnabledEnforceKeyPositionAudit == false && locationIsMatched == false) {
                        //Key position is not enforced and location is not matched
                        setMsg(t("messages.unexpectedKeyAudited", { ref: result.booking.ref, location: props.location.name }));
                        //Play the unexpected sound since this is a standard key audit
                        unexpectedSound({ forceSoundEnabled: true });
                    } else {
                        //Key position is correct and location is matched
                        setMsg(t("messages.keyAudited", { ref: result.booking.ref, location: props.location.name }));
                        goodSound({ forceSoundEnabled: true });
                        if (isEnabledEnforceKeyPositionAudit && result.booking.vehicle.row != null) {
                            //Update the current key position if the key position is correct
                            setCurrentKeyPosition(nKeyPos);
                        }
                    }
                    reloadRemainingKeys(scannedItems);
                } else if (result.type == KeyMangerBarcodeResultType.err) {
                    if (result.err) {
                        setErr(result.err);
                    }
                    badSound({ forceSoundEnabled: true });
                }
                accept(true);
                return;
            });

        });
    }


    const unexpected = scanned.filter((booking) => !isMatchedScan(booking));

    const submit = () => {
        setShowSubmitKeyAudit(false);
        const ids: number[] = [];
        for (const s of scanned) {
            ids.push(s.id);
        }
        submitKeyAudit.send(session.current.context, {
            createDate: localizeDate(new Date()).toISO() ?? "",
            bookingIds: ids,
            locationId: props.location.id,
            isEnforceKeyPositionAudit: isEnabledEnforceKeyPositionAudit,
        }, (result) => {
            if (result == null) {
                return;
            }
            props.onClose(true);
        });
    }

    const scannedList = isEnabledEnforceKeyPositionAudit == true ? scanned.filter((s) => unexpected.includes(s) == false) : scanned;
    const scannedListWithWrongKeyLocationCount = isEnabledEnforceKeyPositionAudit ? scannedList.filter((s) => s.vehicleKey.location?.id != props.location.id).length : 0;

    const assetCounts: IAssetVehiclesForAuditingCountResult = {
        expected: (isEnabledEnforceKeyPositionAudit ? fetchCountV2.data?.expected : fetchCount.data) ?? 0,
        missing: (isEnabledEnforceKeyPositionAudit ? fetchCountV2.data?.missing : null) ?? 0,
    };

    const missingCount = (assetCounts.missing ?? 0) - scannedListWithWrongKeyLocationCount;
    const remainingCount = ((assetCounts.expected) - (scannedList.length - unexpected.length));

    return (
        <Fragment>
            <Dialog
                maxWidth={isEnabledEnforceKeyPositionAudit ? "lg" : "md"}
                fullWidth={true}
                open={props.show}>
                <DialogTitle style={{ textAlign: "center", paddingBottom: 10 }}>
                    <Fragment>
                        <span style={{ color: "gray" }}>{props.enforceKeyPositionAudit == true ? t("keys.labels.keyAuditOrder") : t("keys.labels.keyAuditContents")}</span>
                    </Fragment>
                </DialogTitle>
                <DialogTitle style={{ textAlign: "left", paddingBottom: 10 }}>
                    <Trans
                        i18nKey="keys.labels.scanAllKeys"
                        values={{ location: props.location.name }}
                        components={{
                            bold: <strong />,
                            redText: <span style={{ color: 'red' }} />
                        }}
                    />
                </DialogTitle>
                <DialogContent style={{ minHeight: 50 }}>
                    <div style={flex.flexRows}>
                        <div style={flex.flexFill}>

                            {scanBusy == false && (
                                <BarcodeScanner onError={(err: string) => {
                                    console.log(err);
                                }} onScan={(scan: string) => {
                                    onBarcodeScanned(scan);
                                }} minLength={1} />
                            )}


                            <TextField
                                focused={true}
                                inputProps={{
                                    autoComplete: 'off',
                                    name: "input",
                                }}
                                style={{
                                    width: "100%",
                                }}

                                inputRef={inputRef}
                                type={"search"}
                                size="small"
                                label={t("expressCheckout.labels.barcode")}
                                variant="outlined"
                                value={barcode}
                                onChange={(e) => {
                                    setBarcode(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key == "Enter") {
                                        onBarcodeScanned(barcode);
                                    }
                                }}
                            />
                        </div>

                    </div>


                </DialogContent>

                <DialogContent style={{ minHeight: 60 }}>
                    {msg != null && (
                        <div style={{ padding: 20, backgroundColor: "lightgreen", textAlign: "center" }}>
                            <b>{msg.toUpperCase()}</b>
                        </div>
                    )}
                    {err != null && (
                        <div style={{ padding: 20, backgroundColor: "pink", textAlign: "center" }}>
                            <b>{err.toUpperCase()}</b>
                        </div>
                    )}
                    {fetchScanData.err != null && (
                        <div style={{ padding: 20, backgroundColor: "pink", textAlign: "center" }}>
                            <b>{ErrorToString(fetchScanData.err)}</b>
                        </div>
                    )}
                    {fetchScanData.status == RequestStatus.loading && (
                        <div style={{ padding: 20, backgroundColor: "#eee", textAlign: "center" }}>
                            <b>{t("general.oneMoment")}</b>
                        </div>
                    )}
                </DialogContent>

                <DialogContent style={{ overflow: "hidden" }}>
                    <div style={flex.flexRows}>

                        <div style={flex.flexFill}>
                            <div style={{ textAlign: "center", padding: 5, fontSize: 16 }}>
                                <b style={{ color: "green" }}>{scannedList.length}</b> <b>{t("keys.labels.Scanned")}</b>
                            </div>
                        </div>

                        <div style={flex.flexFill}>
                            <div style={{ marginLeft: 10, marginRight: 10 }}>
                                <div style={{ textAlign: "center", padding: 5, fontSize: 16 }}>
                                    <b style={{ color: "red" }}>{unexpected.length}</b> <b>{t("general.unexpected")}</b>
                                </div>
                            </div>
                        </div>
                        <div style={flex.flexFill}>
                            <div style={{ textAlign: "center", padding: 5, fontSize: 16 }}>
                                <b style={{ color: "orange" }}>{remainingCount}</b> <b>{t("general.remaining")}</b>
                            </div>
                        </div>
                        {isEnabledEnforceKeyPositionAudit && (
                            <div style={flex.flexFill}>
                                <div style={{ textAlign: "center", padding: 5, fontSize: 16, marginLeft: 10 }}>
                                    <b style={{ color: "red" }}>{missingCount}</b> <b>{t("general.missing")}</b>
                                </div>
                            </div>
                        )}
                    </div>


                </DialogContent>
                <DialogContent style={{ height: windowSize.height - 400 }}>
                    <div style={isEnabledEnforceKeyPositionAudit ? {} : { width: 900 }}>
                        <ConfirmationDialog show={removeKey != null} title={t("confirmationDialog.keyAlreadyScanned.title")} message={t("confirmationDialog.keyAlreadyScanned.message")} onClose={(result) => {
                            removeSelectedKey(result);
                        }}>
                        </ConfirmationDialog>

                        <ProgressDialog status={submitKeyAudit.status}></ProgressDialog>
                        <ErrorDialog err={submitKeyAudit.err}></ErrorDialog>
                        <Dialog open={showSubmitKeyAudit}>
                            <DialogTitle>
                                <b>{t("keys.labels.submitAudit")}</b>
                            </DialogTitle>

                            <DialogContent>
                                {unexpected.length > 0 && (
                                    <DialogContentText>
                                        <Trans
                                            i18nKey="keys.dialog.submittingAudit"
                                            values={{ length: unexpected.length, location: props.location.name }}
                                            components={{
                                                bold: <strong />,
                                                redText: <span style={{ color: 'red' }} />,
                                                orangeText: <span style={{ color: 'orange' }} />
                                            }}
                                        />
                                    </DialogContentText>
                                )}
                                {unexpected.length == 0 && (
                                    <DialogContentText>
                                        <Trans i18nKey="keys.dialog.submitKeyAuditConfirmation"
                                            values={{ location: props.location.name }}
                                            components={{
                                                bold: <strong />,
                                                redText: <span style={{ color: 'red' }} />
                                            }}
                                        />
                                    </DialogContentText>
                                )}
                                <div style={{ fontSize: 16 }}>
                                    <div style={{ marginBottom: 10 }}>
                                        <div style={flex.flexRows}>
                                            <div style={flex.flexFill}>
                                                <div style={{ textAlign: "right", paddingRight: 10 }}>{t("keys.labels.Scanned")}:</div>
                                            </div>
                                            <div style={flex.flexFill}>
                                                <b style={{ color: "green" }}>{scannedList.length}</b>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 10 }}>
                                        <div style={flex.flexRows}>
                                            <div style={flex.flexFill}>
                                                <div style={{ textAlign: "right", paddingRight: 10 }}>{t("general.unexpected")}:</div>
                                            </div>
                                            <div style={flex.flexFill}>
                                                <b style={{ color: "orange" }}>{unexpected.length}</b>
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 10 }}>
                                        <div style={flex.flexRows}>
                                            <div style={flex.flexFill}>
                                                <div style={{ textAlign: "right", paddingRight: 10 }}>{t("general.missing")}:</div>
                                            </div>
                                            <div style={flex.flexFill}>
                                                <b style={{ color: "red" }}>{remainingCount + missingCount}</b>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    setShowSubmitKeyAudit(false);
                                }}>
                                    {t("general.back")}
                                </Button>
                                <Button onClick={() => {
                                    submit();
                                }}>
                                    <b>{t("general.submit")}</b>
                                </Button>
                            </DialogActions>
                        </Dialog>
                        {selectedBooking != null && (<Dialog open={showMissingKeyMenu}>
                            <DialogTitle>
                                <b>{selectedBooking.ref}</b>
                            </DialogTitle>
                            <DialogContent>
                                <div>{formatVehicle(selectedBooking?.vehicle)}</div>
                                <div style={{ marginTop: 20 }}>
                                    <Button fullWidth={true} variant="contained" color="secondary" onClick={() => {
                                        setShowMissingKeyMenu(false);
                                        window.open(paths.booking(selectedBooking.id));
                                    }}>
                                        <b>{t("keys.labels.openBooking")}</b>
                                    </Button>
                                </div>
                                {isMarkedAsMissing(selectedBooking) == false && (
                                    <div style={{ marginTop: 20 }}>
                                        <Button
                                            disabled={showMissingKeyMenu == false}
                                            fullWidth={true}
                                            variant="contained"
                                            onClick={() => {

                                                setShowMissingKeyMenu(false);
                                                setMissingKeys([...missingKeys, selectedBooking]);
                                            }}>
                                            <b>{t("keys.labels.keyMarkAsMissing")}</b>
                                        </Button>
                                    </div>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    setShowMissingKeyMenu(false);
                                }}>
                                    {t("general.cancel")}
                                </Button>
                            </DialogActions>
                        </Dialog>)}
                        <div>
                            <div style={flex.flexRows}>
                                <div style={flex.flexFill}>
                                    {
                                        scannedList.map((booking, key) => <AuditListItem
                                            row={isEnabledEnforceKeyPositionAudit ? booking.vehicle.row : null}
                                            icon={isEnabledEnforceKeyPositionAudit ? null : unexpected.indexOf(booking) == -1 ? FiCheck : FiAlertOctagon}
                                            booking={booking} key={key} onClick={() => {
                                                window.open(paths.booking(booking.id));
                                            }}></AuditListItem>)
                                    }
                                </div>
                                <div style={flex.flexFill}>
                                    <div style={{ marginLeft: 10, marginRight: 10 }}>
                                        {
                                            unexpected.map((booking, key) => {
                                                return <AuditListItem
                                                    icon={isEnabledEnforceKeyPositionAudit ? (booking.auditIssueCount ?? 0 > 0 ? FiAlertTriangle : null) : FiAlertOctagon}
                                                    booking={booking}
                                                    key={key}
                                                    onClick={() => {
                                                        window.open(paths.booking(booking.id));
                                                    }}></AuditListItem>
                                            })
                                        }
                                    </div>
                                </div>
                                <div style={flex.flexFill}>
                                    {isEnabledEnforceKeyPositionAudit == false && params != null && (
                                        <PagedDataView<IAssetKeysAtLocationPath, Booking>
                                            animate={false}
                                            apiHook={api.hooks.assetVehicleKey.getAtLocation}
                                            parameters={params}
                                            renderItem={(booking) => <AuditListItem
                                                row={isEnabledEnforceKeyPositionAudit ? booking.vehicle.row : null}
                                                icon={(booking.auditIssueCount ?? 0) > 0 ? FiAlertTriangle : null}
                                                booking={booking} onClick={() => {
                                                    window.open(paths.booking(booking.id));
                                                }} />}
                                        ></PagedDataView>
                                    )}
                                    {isEnabledEnforceKeyPositionAudit == true && expectedParams != null && (
                                        <PagedDataView<IAssetVehiclesForAuditingAtLocationId, Booking>
                                            animate={false}
                                            apiHook={api.hooks.keyManager.getVehiclesForAuditingAtLocationId}
                                            parameters={expectedParams}
                                            renderItem={(booking) => <AuditListItem
                                                row={isEnabledEnforceKeyPositionAudit ? booking.vehicle.row : null}
                                                icon={(booking.auditIssueCount ?? 0) > 0 ? FiAlertTriangle : null}
                                                booking={booking} onClick={() => {
                                                    window.open(paths.booking(booking.id));
                                                }} />}
                                        ></PagedDataView>
                                    )}
                                </div>
                                {isEnabledEnforceKeyPositionAudit && (<div style={flex.flexFill}>
                                    {missingParams != null && (
                                        <div style={{ marginLeft: 10 }}>
                                            <PagedDataView<IAssetVehiclesForAuditingAtLocationId, Booking>
                                                animate={false}
                                                apiHook={api.hooks.keyManager.getVehiclesForAuditingAtLocationId}
                                                parameters={missingParams}
                                                renderItem={(booking) => <AuditListItem
                                                    row={isEnabledEnforceKeyPositionAudit ? booking.vehicle.row : null}
                                                    icon={(booking.auditIssueCount ?? 0) > 0 ? FiAlertTriangle : null}
                                                    isMarkedAsMissing={isMarkedAsMissing(booking)}
                                                    warning={[booking.vehicleKey?.location?.parentName, booking.vehicleKey?.location?.name].filter((f) => `${f ?? ""}`.length > 0).join(" / ")}
                                                    booking={booking} onClick={() => {
                                                        if (isMarkedAsMissing(booking) || isMatchedScan(booking) == false || (booking.vehicle.row ?? 0) == 0) {
                                                            window.open(paths.booking(booking.id));
                                                            return;
                                                        }
                                                        setShowMissingKeyMenu(true);
                                                        setSelectedBooking(booking);
                                                    }} />}
                                            ></PagedDataView>
                                        </div>
                                    )}

                                </div>)}
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        props.onClose(false);
                    }}>
                        {t("general.cancel")}
                    </Button>
                    <Button onClick={() => {
                        setShowSubmitKeyAudit(true);
                    }}>
                        <b>{t("keys.labels.submitKeyAudit")}</b>
                    </Button>
                </DialogActions>
            </Dialog>


        </Fragment>
    );
}

KeyAuditScanner.propTypes = {
    location: propTypes.any.isRequired,
    onClose: propTypes.func.isRequired,
    show: propTypes.bool.isRequired
}

export default KeyAuditScanner;